// dashboard/composables/useAutoResolve.js
import { ref, computed, watch, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import AgentAPI from 'dashboard/api/agents';

/**
 * Composable para gerenciar configurações de Auto Resolve
 * Suporta configurações por: Padrão, Etiqueta, Agente e Time
 */
export function useAutoResolve() {
  const { t } = useI18n();
  const store = useStore();
  const { currentAccount, updateAccount } = useAccount();

  // ============================================
  // ESTADO PRINCIPAL
  // ============================================
  const isEnabled = ref(false);
  const isSubmitting = ref(false);
  const activeTab = ref('padrão');

  // ============================================
  // DADOS DO STORE
  // ============================================
  const labels = useMapGetter('labels/getLabels');
  const teams = useMapGetter('teams/getTeams');

  // Lista própria (não o store `agents/getAgents`): o config por agente casa
  // com conversation.assignee_id, e agentes de IA do Studio (bot users,
  // account_users.is_human=false) também são assignees — o store só tem
  // humanos e escondia os de IA do select e das configs já salvas.
  const agents = ref([]);

  // ============================================
  // CONFIGURAÇÃO PADRÃO
  // ============================================
  const defaultConfig = ref({
    duration: 0,
    unit: 'minutes',
    message: '',
    labelToApply: {},
    ignoreWaiting: false,
  });

  // ============================================
  // CONFIGURAÇÕES ESPECÍFICAS (Maps)
  // ============================================
  const labelConfigs = ref(new Map());
  const agentConfigs = ref(new Map());
  const teamConfigs = ref(new Map());

  // ============================================
  // CONSTANTES
  // ============================================
  const DURATION_OPTIONS = [
    { value: 'minutes', label: 'Minutos' },
    { value: 'hours', label: 'Horas' },
    { value: 'days', label: 'Dias' },
  ];

  const CONFIG_TYPES = {
    LABEL: 'label',
    AGENT: 'agent',
    TEAM: 'team',
  };

  // ============================================
  // FUNÇÕES DE CONVERSÃO
  // ============================================
  const convertToMinutes = (value, unit) => {
    if (unit === 'hours') return value * 60;
    if (unit === 'days') return value * 1440;
    return value;
  };

  const convertFromMinutes = minutes => {
    if (minutes >= 1440 && minutes % 1440 === 0) {
      return { value: minutes / 1440, unit: 'days' };
    }
    if (minutes >= 60 && minutes % 60 === 0) {
      return { value: minutes / 60, unit: 'hours' };
    }
    return { value: minutes, unit: 'minutes' };
  };

  const formatFriendlyDuration = minutes => {
    if (minutes < 60) {
      return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    }
    if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hora${hours !== 1 ? 's' : ''}`;
      }
      return `${hours}h ${remainingMinutes}min`;
    }
    const days = Math.floor(minutes / 1440);
    const remainingHours = Math.floor((minutes % 1440) / 60);
    const remainingMinutes = minutes % 60;
    let result = `${days} dia${days !== 1 ? 's' : ''}`;
    if (remainingHours > 0) result += ` ${remainingHours}h`;
    if (remainingMinutes > 0) result += ` ${remainingMinutes}min`;
    return result;
  };

  // ============================================
  // COMPUTED - OPTIONS
  // ============================================
  const labelOptions = computed(() => {
    if (!labels.value?.length) return [];
    return labels.value.map(label => ({
      id: label.title,
      name: label.title,
      color: label.color,
      icon: h('span', {
        class:
          'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
        style: { backgroundColor: label.color },
      }),
    }));
  });

  const labelOptionsForConfig = computed(() => {
    if (!labels.value?.length) return [];
    return labels.value
      .filter(label => !labelConfigs.value.has(label.title))
      .map(label => ({
        id: label.title,
        name: label.title,
        color: label.color,
        icon: h('span', {
          class:
            'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
          style: { backgroundColor: label.color },
        }),
      }));
  });

  const agentOptions = computed(() => {
    if (!agents.value?.length) return [];
    return agents.value
      .filter(agent => !agentConfigs.value.has(agent.id))
      .map(agent => ({
        id: agent.id,
        name: agent.name,
        email: agent.email,
      }));
  });

  const teamOptions = computed(() => {
    if (!teams.value?.length) return [];
    return teams.value
      .filter(team => !teamConfigs.value.has(team.id))
      .map(team => ({
        id: team.id,
        name: team.name,
        description: team.description,
      }));
  });

  const hasSpecificConfigs = computed(() => {
    return (
      labelConfigs.value.size > 0 ||
      agentConfigs.value.size > 0 ||
      teamConfigs.value.size > 0
    );
  });

  const selectedLabelName = computed(() => {
    return defaultConfig.value.labelToApply?.name ?? null;
  });

  // ============================================
  // API - FUNÇÕES DE PERSISTÊNCIA
  // ============================================
  const loadRequiredData = async () => {
    try {
      const [agentsResponse] = await Promise.all([
        AgentAPI.listAll(),
        store.dispatch('teams/get'),
        store.dispatch('labels/get'),
      ]);
      agents.value = agentsResponse.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar dados:', error);
    }
  };

  const updateAccountSettings = async settings => {
    try {
      isSubmitting.value = true;
      await updateAccount(settings, { silent: true });
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.SUCCESS'));
    } catch (error) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.ERROR'));
      throw error;
    } finally {
      isSubmitting.value = false;
    }
  };

  // ============================================
  // CONFIGURAÇÃO PADRÃO - CRUD
  // ============================================
  const saveDefaultConfig = async () => {
    const durationInMinutes = convertToMinutes(
      defaultConfig.value.duration,
      defaultConfig.value.unit
    );

    if (durationInMinutes < 10) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
      return Promise.resolve();
    }

    return updateAccountSettings({
      auto_resolve_after: durationInMinutes,
      auto_resolve_message: defaultConfig.value.message,
      auto_resolve_ignore_waiting: defaultConfig.value.ignoreWaiting,
      auto_resolve_label: selectedLabelName.value,
    });
  };

  const disableAutoResolve = async () => {
    defaultConfig.value = {
      duration: null,
      unit: 'minutes',
      message: '',
      labelToApply: {},
      ignoreWaiting: false,
    };

    labelConfigs.value.clear();
    agentConfigs.value.clear();
    teamConfigs.value.clear();

    return updateAccountSettings({
      auto_resolve_after: null,
      auto_resolve_message: '',
      auto_resolve_ignore_waiting: false,
      auto_resolve_label: null,
      label_auto_resolve_configs: {},
      agent_auto_resolve_configs: {},
      team_auto_resolve_configs: {},
    });
  };

  const toggleAutoResolve = async () => {
    if (!isEnabled.value) {
      await disableAutoResolve();
    }
  };

  // ============================================
  // CONFIGURAÇÕES ESPECÍFICAS - SAVE HELPERS
  // ============================================
  const buildConfigPayload = (configMap, keyTransform = k => k) => {
    const configs = {};
    configMap.forEach((config, key) => {
      configs[keyTransform(key)] = {
        auto_resolve_after: config.duration,
        auto_resolve_message: config.message,
        auto_resolve_ignore_waiting: config.ignoreWaiting,
        auto_resolve_label: config.label?.name || null,
      };
    });
    return configs;
  };

  const saveLabelConfigs = async () => {
    const configs = buildConfigPayload(labelConfigs.value);
    const currentSettings = currentAccount.value?.settings || {};
    return updateAccountSettings({
      ...currentSettings,
      label_auto_resolve_configs: configs,
    });
  };

  const saveAgentConfigs = async () => {
    const configs = buildConfigPayload(agentConfigs.value);
    const currentSettings = currentAccount.value?.settings || {};
    return updateAccountSettings({
      ...currentSettings,
      agent_auto_resolve_configs: configs,
    });
  };

  const saveTeamConfigs = async () => {
    const configs = buildConfigPayload(teamConfigs.value);
    const currentSettings = currentAccount.value?.settings || {};
    return updateAccountSettings({
      ...currentSettings,
      team_auto_resolve_configs: configs,
    });
  };

  // ============================================
  // CONFIGURAÇÕES ESPECÍFICAS - CRUD GENÉRICO
  // ============================================

  const getConfigMap = type => {
    switch (type) {
      case CONFIG_TYPES.LABEL:
        return labelConfigs;
      case CONFIG_TYPES.AGENT:
        return agentConfigs;
      case CONFIG_TYPES.TEAM:
        return teamConfigs;
      default:
        throw new Error(`Tipo de configuração inválido: ${type}`);
    }
  };

  const getSaveFunction = type => {
    switch (type) {
      case CONFIG_TYPES.LABEL:
        return saveLabelConfigs;
      case CONFIG_TYPES.AGENT:
        return saveAgentConfigs;
      case CONFIG_TYPES.TEAM:
        return saveTeamConfigs;
      default:
        throw new Error(`Tipo de configuração inválido: ${type}`);
    }
  };

  /**
   * Adiciona ou atualiza uma configuração específica
   * @param {string} type - 'label' | 'agent' | 'team'
   * @param {string|number} key - identificador (labelName, agentId, teamId)
   * @param {object} config - configuração a ser salva
   */
  const upsertConfig = async (type, key, config) => {
    const durationInMinutes = convertToMinutes(config.duration, config.unit);

    if (durationInMinutes < 5) {
      useAlert('Duração mínima de 5 minutos');
      return { success: false };
    }

    const configData = {
      name: config.name,
      color: config.color,
      duration: durationInMinutes,
      message: config.message,
      label: config.labelToApply,
      ignoreWaiting: config.ignoreWaiting,
    };

    const configMap = getConfigMap(type);
    const oldConfig = configMap.value.get(key);
    configMap.value.set(key, configData);

    try {
      await getSaveFunction(type)();
      return { success: true };
    } catch (error) {
      // Rollback
      if (oldConfig) {
        configMap.value.set(key, oldConfig);
      } else {
        configMap.value.delete(key);
      }
      return { success: false, error };
    }
  };

  /**
   * Remove uma configuração específica
   * @param {string} type - 'label' | 'agent' | 'team'
   * @param {string|number} key - identificador
   */
  const removeConfig = async (type, key) => {
    const configMap = getConfigMap(type);
    const backup = new Map(configMap.value);
    configMap.value.delete(key);

    try {
      await getSaveFunction(type)();
      return { success: true };
    } catch (error) {
      // Rollback
      configMap.value.clear();
      backup.forEach((config, k) => configMap.value.set(k, config));
      return { success: false, error };
    }
  };

  /**
   * Obtém uma configuração para edição
   * @param {string} type - 'label' | 'agent' | 'team'
   * @param {string|number} key - identificador
   */
  const getConfigForEdit = (type, key) => {
    const configMap = getConfigMap(type);
    const config = configMap.value.get(key);
    if (!config) return null;

    const converted = convertFromMinutes(config.duration);
    return {
      name: config.name,
      color: config.color,
      duration: converted.value,
      unit: converted.unit,
      message: config.message,
      labelToApply: config.label || {},
      ignoreWaiting: config.ignoreWaiting,
    };
  };

  // ============================================
  // HELPERS INTERNOS
  // ============================================
  const findLabelWithColor = labelName => {
    return labelOptions.value.find(l => l.name === labelName) || {};
  };

  // ============================================
  // WATCH - CARREGAR DADOS DO ACCOUNT
  // ============================================
  watch(
    [currentAccount, labelOptions],
    () => {
      const settings = currentAccount.value?.settings || {};
      const {
        auto_resolve_after,
        auto_resolve_message,
        auto_resolve_ignore_waiting,
        auto_resolve_label,
        label_auto_resolve_configs,
        agent_auto_resolve_configs,
        team_auto_resolve_configs,
      } = settings;

      // Carregar configuração padrão
      if (auto_resolve_after) {
        const converted = convertFromMinutes(auto_resolve_after);
        defaultConfig.value.duration = converted.value;
        defaultConfig.value.unit = converted.unit;
      }
      defaultConfig.value.message = auto_resolve_message || '';
      defaultConfig.value.ignoreWaiting = auto_resolve_ignore_waiting || false;
      defaultConfig.value.labelToApply =
        labelOptions.value.find(opt => opt.name === auto_resolve_label) || {};

      // Carregar configurações de labels
      labelConfigs.value.clear();
      if (label_auto_resolve_configs) {
        Object.entries(label_auto_resolve_configs).forEach(
          ([labelName, config]) => {
            const label = labels.value?.find(l => l.title === labelName);
            if (label) {
              labelConfigs.value.set(labelName, {
                name: labelName,
                color: label.color,
                duration: config.auto_resolve_after,
                message: config.auto_resolve_message,
                label: findLabelWithColor(config.auto_resolve_label),
                ignoreWaiting: config.auto_resolve_ignore_waiting,
              });
            }
          }
        );
      }

      // Carregar configurações de agentes
      agentConfigs.value.clear();
      if (agent_auto_resolve_configs) {
        Object.entries(agent_auto_resolve_configs).forEach(
          ([agentId, config]) => {
            const agent = agents.value?.find(
              a => a.id === parseInt(agentId, 10)
            );
            if (agent) {
              agentConfigs.value.set(parseInt(agentId, 10), {
                name: agent.name,
                duration: config.auto_resolve_after,
                message: config.auto_resolve_message,
                label: findLabelWithColor(config.auto_resolve_label),
                ignoreWaiting: config.auto_resolve_ignore_waiting,
              });
            }
          }
        );
      }

      // Carregar configurações de times
      teamConfigs.value.clear();
      if (team_auto_resolve_configs) {
        Object.entries(team_auto_resolve_configs).forEach(
          ([teamId, config]) => {
            const team = teams.value?.find(
              tm => tm.id === parseInt(teamId, 10)
            );
            if (team) {
              teamConfigs.value.set(parseInt(teamId, 10), {
                name: team.name,
                duration: config.auto_resolve_after,
                message: config.auto_resolve_message,
                label: findLabelWithColor(config.auto_resolve_label),
                ignoreWaiting: config.auto_resolve_ignore_waiting,
              });
            }
          }
        );
      }

      // Determinar se está habilitado
      isEnabled.value = !!(
        auto_resolve_after ||
        labelConfigs.value.size > 0 ||
        agentConfigs.value.size > 0 ||
        teamConfigs.value.size > 0
      );
    },
    { deep: true, immediate: true }
  );

  // ============================================
  // RETORNO PÚBLICO
  // ============================================
  return {
    // Estado
    isEnabled,
    isSubmitting,
    activeTab,

    // Configuração padrão
    defaultConfig,

    // Configurações específicas
    labelConfigs,
    agentConfigs,
    teamConfigs,

    // Options para selects
    labelOptions,
    labelOptionsForConfig,
    agentOptions,
    teamOptions,

    // Computed
    hasSpecificConfigs,
    selectedLabelName,

    // Constantes
    DURATION_OPTIONS,
    CONFIG_TYPES,

    // Funções de conversão
    convertToMinutes,
    convertFromMinutes,
    formatFriendlyDuration,

    // Funções principais
    loadRequiredData,
    toggleAutoResolve,
    saveDefaultConfig,

    // CRUD genérico para configs específicas
    upsertConfig,
    removeConfig,
    getConfigForEdit,

    // Helpers
    findLabelWithColor,

    // Dados brutos (para casos específicos)
    labels,
    agents,
    teams,
  };
}
